'use client'

import { signIn, signUp } from '@repo/auth/client'
import { Eye, EyeOff } from 'lucide-react'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

import { FlouWordmark } from '@/components/atoms/flou-wordmark'
import {
  AUTH_ACCENT_GLOW,
  AUTH_ACCENT_GRADIENT,
  AuthCard,
  AuthDivider,
  AuthField,
} from '@/components/molecules/auth-card'
import { ForgotPasswordModal } from '@/components/molecules/forgot-password-modal.client'
import { SocialAuthButtons } from '@/components/molecules/social-auth-buttons.client'
import { authErrorMessage } from '@/lib/auth-errors'
import { PICKED_RING } from '@/lib/surface'
import { ACCENT_TEXT, BODY, MICRO, SUBHEADING } from '@/lib/typography'
import { cn } from '@/lib/utils'

type Props = {
  locale: string
  /** Provider ids whose keys the server actually holds — see `SocialAuthButtons`. */
  socialProviders?: readonly string[]
  /**
   * The pre-launch curtain, resolved on the server.
   *
   * This card is the site's SECOND registration surface — the tab switch beside
   * "Logowanie" posts to the same endpoint `/pl/register` does. With signups
   * paused the whole register half is gone rather than disabled: a tab that
   * opens a form which cannot be submitted is a longer way of saying no.
   */
  signUpDisabled?: boolean
  /** A refusal the visitor was redirected here with, already in Polish. */
  notice?: string
}

type Mode = 'login' | 'register'
type Fault = { title: string; detail: string }

const TABS = [
  { id: 'login' as const, label: 'Logowanie' },
  { id: 'register' as const, label: 'Rejestracja' },
]

/** How long the crossing runs before the browser actually leaves. */
const CROSSING_MS = 420

/**
 * The sign-in screen — one column, one card, one thing to do.
 *
 * ## IT WAS A LANDING PAGE WITH A FORM STAPLED TO IT
 *
 * Two columns: a 2.4rem marketing headline, a paragraph and three gradient icon
 * chips pitching an account to somebody who already has one, and the form
 * pushed into a 22rem gutter on the right. Measured on a 375×812 phone, that
 * put `#auth-email` at 1011px — a visitor scrolled two screens of sales copy to
 * reach the box they came for. Everyone who lands here either has a password or
 * was bounced here by the panel's redirect. Neither needs selling.
 *
 * So the pitch column is gone and the card is the page. What it carried has not
 * been thrown away: the headline is now the page's `<h1>` (it was an `<h2>`
 * sitting ABOVE the real `<h1>`, an inverted outline), and the one sentence
 * that named what is actually behind the door is the line under it. It has
 * since been tightened to lead with the nouns rather than with the container —
 * „Zamówienia, terminy, pliki i rozmowy w jednym miejscu." — which is the
 * owner's wording. The two chips that pitched services the marketing pages
 * already sell are not here any more.
 *
 * ## THE SPECTACLE STAYS BEHIND THE GLASS
 *
 * `AuthBackdrop` is the one WebGL context on this route and it is the "new
 * world" the owner asked for. A card that also performs competes with it, so
 * this one does not: it arrives once, and the only other motion on the screen
 * is the crossing — see {@link CROSSING_MS}. Nothing loops beside a password
 * field.
 *
 * ## THE FORM IS BETTER AS A FORM, NOT ONLY PRETTIER
 *
 * A refusal used to fly to a toast in the corner, a long way from the field
 * that was wrong. It is an `role="alert"` at the top of the form now, with the
 * password selected so retyping is one gesture — and the toast is DELETED
 * rather than joined, because `e2e/auth.spec.ts` matches `getByRole('alert')`
 * and two of them is a strict-mode failure. The mode switch declared
 * `role="tablist"` and implemented none of the contract; it has arrow keys, a
 * roving tabindex and a real `tabpanel` now, because the same spec addresses
 * those tabs by role and the honest fix is to keep the promise.
 */
export function LoginPageView({
  locale,
  socialProviders = [],
  signUpDisabled = false,
  notice,
}: Props) {
  const [mode, setMode] = useState<Mode>('login')
  const [forgotEmail, setForgotEmail] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [crossing, setCrossing] = useState(false)
  const [fault, setFault] = useState<Fault | null>(null)
  const [reveal, setReveal] = useState(false)

  /**
   * The fields are UNCONTROLLED, and that is the fix for a real failure.
   *
   * They used to be `value={email}` against React state, which meant that
   * anything typed before the bundle finished loading was wiped the moment
   * React attached: hydration writes the state's value — empty — over whatever
   * is in the box. On a slow phone that is the whole first second of the page.
   * The visitor typed an address, watched it vanish, or worse pressed the button
   * and got "nie zalogowano" for an address the server never received.
   *
   * Uncontrolled inputs are left alone by hydration, so the refs below are the
   * only honest source of what the visitor actually typed.
   */
  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  /**
   * Is React listening yet?
   *
   * Sign-in happens in the browser, so the button genuinely cannot work before
   * the bundle arrives — and left enabled it does something WORSE than nothing:
   * a `type="submit"` inside a `<form>` with no `action` performs a native GET
   * to the same address, which reloads the page and throws away everything
   * typed into it. The visitor presses "Zaloguj się", the screen blinks, and
   * the form is empty with no explanation.
   *
   * Disabled until mounted closes both routes to that — the click and the
   * Enter key, which the HTML spec routes through this same default button.
   */
  const [ready, setReady] = useState(false)
  useEffect(() => setReady(true), [])

  /**
   * Leave, but through the door rather than past it.
   *
   * The successful sign-in is the only moment on this page that is literally
   * entering somewhere else, and it was a bare `location.href` — a hard
   * navigation and a white flash. The card recedes while the brand blooms
   * through it, then the browser goes. Somebody who asked not to be animated at
   * gets the navigation immediately and nothing else.
   */
  const cross = (href: string) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.location.href = href
      return
    }
    setCrossing(true)
    window.setTimeout(() => {
      window.location.href = href
    }, CROSSING_MS)
  }

  const refuse = (title: string, detail: string, field: 'email' | 'password') => {
    setFault({ title, detail })
    const box = (field === 'email' ? emailRef : passwordRef).current
    box?.focus()
    box?.select()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || crossing) return

    const email = emailRef.current?.value.trim() ?? ''
    const password = passwordRef.current?.value ?? ''

    // Asked here rather than left to the server: an empty box is not a failed
    // sign-in, it is an unfinished form, and sending it burns one of the three
    // attempts the endpoint allows per ten seconds.
    if (!email || !password) {
      refuse('Brakuje danych', 'Podaj adres e-mail i hasło.', email ? 'password' : 'email')
      return
    }

    setFault(null)
    setBusy(true)
    try {
      if (mode === 'register') {
        const name = nameRef.current?.value.trim() ?? ''
        const res = await signUp.email({ email, password, name: name || email })
        if (res.error) {
          refuse(
            'Nie udało się założyć konta',
            authErrorMessage(res.error, 'Spróbuj ponownie.'),
            'email',
          )
          return
        }
        cross(`/${locale}/panel`)
        return
      }

      const res = await signIn.email({ email, password })
      if (res.error) {
        refuse(
          'Nie udało się zalogować',
          authErrorMessage(res.error, 'Sprawdź adres e-mail i hasło.'),
          'password',
        )
        return
      }
      // One panel for everyone. It used to fork here — staff to `/panel`,
      // clients to `/dashboard` — which meant this component had to know the
      // role catalogue to send someone to the right address. Now the panel
      // decides for itself which sections exist.
      cross(`/${locale}/panel`)
    } finally {
      setBusy(false)
    }
  }

  /** The tablist contract the roles were promising: arrows move and select. */
  const onTabKey = (e: React.KeyboardEvent) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = TABS[(TABS.findIndex((t) => t.id === mode) + step + TABS.length) % TABS.length]
    if (!next) return
    setMode(next.id)
    tabRefs.current[TABS.indexOf(next)]?.focus()
  }

  const locked = busy || crossing || !ready

  return (
    // No section background: the body's flat brand black is the background
    // everywhere now — auth is the same world, just quieter.
    //
    // `overflow-x-clip`, NOT `overflow-hidden`: the card's glow reaches past its
    // own box, and clipping both axes cut it along a straight line, which is
    // what makes a glow look like it stopped rather than faded.
    //
    // `items-start` and a small top pad: the content begins just under the bar
    // rather than being centred in whatever height is left. Centring put the
    // card a third of the way down a tall window and below the fold on a short
    // one — two different layouts from one rule.
    <div className="relative flex w-full flex-1 items-start justify-center overflow-x-clip px-6 pt-1 pb-16">
      <div
        className={cn(
          'relative z-10 flex w-full max-w-[27rem] flex-col',
          crossing && 'flou-auth-leave',
        )}
      >
        {/* Bigger and tighter, by the owner's note: *„lepiej to poukładaj,
            mniejsze odstępy, większe trochę"*. `SUBHEADING` alone left the
            display face on the body's default line-height, so a two-line
            heading opened a gap wider than the letters are tall; `text-balance`
            then evens the two lines instead of leaving three words alone on the
            second. */}
        {/* TWO EXPLICIT LINES, not a wrap.

            `text-balance` was deciding where this broke, which meant the break
            moved with the viewport and the conjunction „i" kept landing alone
            at the head of the second line with the pink clause behind it — a
            single white letter stranded between two things it belongs to.
            Owner: *„i musi być w nowej linii, bo obecnie jest to sierota,
            mniejszy odstęp między liniami"*.

            So the break is a `block` per clause: line one is the address, line
            two is the promise, at every width. „i" goes INTO the accent, which
            is what removes the stranding — the second line is now one coloured
            phrase rather than a loose letter in front of one.

            The `pb` is not spacing. `background-clip: text` paints only inside
            the element's background box, and at `leading-[1.05]` that box is
            shorter than the glyphs standing in it — the descenders of „yższy"
            would get no gradient and reach the screen sliced off. The negative
            margin gives the space straight back, so the two lines sit where the
            leading says they do. */}
        <h1
          className={cn(
            SUBHEADING,
            'text-center text-[1.75rem] leading-[1.05] text-white sm:text-[2.25rem]',
          )}
        >
          <span className="block">Wejdź do świata FLOU</span>
          <span className={cn(ACCENT_TEXT, 'block pb-[0.14em] -mb-[0.14em]')}>
            i wskocz na wyższy poziom.
          </span>
        </h1>
        <p
          className={cn(BODY, 'mx-auto mt-2 max-w-[23rem] text-center text-balance text-white/60')}
        >
          Zamówienia, terminy, pliki i rozmowy w jednym miejscu.
        </p>

        <AuthCard seeThrough className="flou-auth-enter mt-4 p-6 sm:p-7">
          {/* The mark stands where an „Strefa klienta" eyebrow used to, and it
              is the whole of the brand on this card. The page carried a 4.45rem
              wordmark above the columns before; at that size, a second one
              under a header that already shows the mark is the same object
              twice, and it cost 150px of the vertical the form needed. */}
          <FlouWordmark className="mx-auto h-6 w-auto text-white/90" />

          {notice ? (
            <p
              className={cn(
                MICRO,
                'mt-5 rounded-xl border border-amber-400/25 bg-amber-500/[0.08] px-4 py-3 leading-relaxed text-amber-100/85',
              )}
            >
              {notice}
            </p>
          ) : null}

          {/* Mode switch — absent while signups are paused. */}
          <div
            role="tablist"
            aria-label="Logowanie lub rejestracja"
            hidden={signUpDisabled}
            onKeyDown={onTabKey}
            className="mt-5 grid grid-cols-2 gap-1 rounded-full border border-white/[0.12] bg-white/[0.04] p-1"
          >
            {TABS.map((t, i) => {
              const on = mode === t.id
              return (
                <button
                  key={t.id}
                  ref={(el) => {
                    tabRefs.current[i] = el
                  }}
                  type="button"
                  role="tab"
                  id={`auth-tab-${t.id}`}
                  aria-selected={on}
                  aria-controls="auth-panel"
                  tabIndex={on ? 0 : -1}
                  onClick={() => setMode(t.id)}
                  className={cn(
                    MICRO,
                    'rounded-full border border-transparent py-2 font-semibold transition',
                    on ? cn(PICKED_RING, 'text-white') : 'text-white/60 hover:text-white',
                  )}
                >
                  {t.label}
                </button>
              )
            })}
          </div>

          <div id="auth-panel" role="tabpanel" aria-labelledby={`auth-tab-${mode}`}>
            <h2 className={cn(BODY, 'mt-6 mb-5 text-center font-semibold text-white')}>
              {/* „Załóż kartę twórcy" had an audience baked into it and the
                  account does not: the same form is used by firms. Owner's
                  note — *„nie karta twórcy, bo może to być firma"*. „Załóż
                  konto" is what `/pl/register` already calls the same act, so
                  the two surfaces stop naming one thing two ways. */}
              {mode === 'login' ? 'Witaj ponownie' : 'Załóż konto'}
            </h2>

            <SocialAuthButtons enabled={socialProviders} callbackURL={`/${locale}/panel`} />

            <AuthDivider />

            <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
              {/* The refusal, where the refused thing is. Not a toast: a message
                  in the corner of the screen is read by the people who least
                  need it and missed by the one retyping a password. */}
              {fault ? (
                <p
                  role="alert"
                  id="auth-fault"
                  className={cn(
                    MICRO,
                    'rounded-xl border border-[oklch(0.7_0.2_20)]/35 bg-[oklch(0.55_0.2_20)]/12 px-4 py-3 leading-relaxed text-[oklch(0.9_0.08_20)]',
                  )}
                >
                  <span className="font-semibold">{fault.title}.</span> {fault.detail}
                </p>
              ) : null}

              {mode === 'register' ? (
                <AuthField
                  id="auth-name"
                  name="name"
                  label="Imię"
                  type="text"
                  ref={nameRef}
                  placeholder="np. Ola"
                  autoComplete="given-name"
                  className="text-[16px] sm:text-[0.95rem]"
                />
              ) : null}

              <AuthField
                id="auth-email"
                name="email"
                label="Adres e-mail"
                type="email"
                inputMode="email"
                ref={emailRef}
                placeholder="ty@example.com"
                autoComplete="email"
                aria-invalid={fault ? true : undefined}
                aria-describedby={fault ? 'auth-fault' : undefined}
                // 16px, and not for looks: anything under it makes iOS Safari
                // zoom the viewport the moment the field takes focus, and the
                // visitor lands on a page they now have to pinch back out of.
                className="text-[16px] sm:text-[0.95rem]"
              />

              <AuthField
                id="auth-password"
                name="password"
                label="Hasło"
                type={reveal ? 'text' : 'password'}
                ref={passwordRef}
                placeholder="••••••••"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                aria-invalid={fault ? true : undefined}
                aria-describedby={fault ? 'auth-fault' : undefined}
                className="pr-12 text-[16px] sm:text-[0.95rem]"
                rightSlot={
                  <button
                    type="button"
                    onClick={() => setReveal((v) => !v)}
                    aria-label={reveal ? 'Ukryj hasło' : 'Pokaż hasło'}
                    className="flex size-7 items-center justify-center rounded-lg text-white/50 transition-colors hover:text-white"
                  >
                    {reveal ? (
                      <EyeOff aria-hidden="true" className="size-4" />
                    ) : (
                      <Eye aria-hidden="true" className="size-4" />
                    )}
                  </button>
                }
              />

              {mode === 'login' ? (
                <button
                  type="button"
                  onClick={() => setForgotEmail(emailRef.current?.value.trim() ?? '')}
                  className={cn(
                    MICRO,
                    'inline-flex min-h-6 items-center self-start text-white/55 transition-colors hover:text-white/70',
                  )}
                >
                  Zapomniałeś hasła?
                </button>
              ) : (
                <p className={cn(MICRO, 'leading-relaxed text-white/55')}>
                  Zakładając konto akceptujesz regulamin i politykę prywatności FLOU.
                </p>
              )}

              {/* Before hydration this is correctly disabled, and it used to
                  LOOK broken: the brand gradient at 60% opacity is mud, under a
                  `not-allowed` cursor, saying "Zaloguj się". Flat and quiet
                  reads as not-yet rather than as failed. */}
              <button
                type="submit"
                disabled={locked}
                className={cn(
                  BODY,
                  'mt-1 w-full rounded-xl py-3.5 font-semibold transition-all duration-300',
                  locked
                    ? 'cursor-progress bg-white/[0.06] text-white/45'
                    : 'text-white hover:brightness-110 active:scale-[0.99]',
                )}
                style={
                  locked
                    ? undefined
                    : { background: AUTH_ACCENT_GRADIENT, boxShadow: AUTH_ACCENT_GLOW }
                }
              >
                {busy || crossing
                  ? mode === 'login'
                    ? 'Loguję…'
                    : 'Zakładam konto…'
                  : mode === 'login'
                    ? 'Zaloguj się'
                    : 'Załóż konto'}
              </button>
            </form>
          </div>

          <p className={cn(MICRO, 'mt-5 text-center text-white/55')}>
            {signUpDisabled ? (
              'Zapisy nowych kont są chwilowo wstrzymane. Otwieramy się wkrótce.'
            ) : mode === 'login' ? (
              <>
                Nie masz konta?{' '}
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className="font-semibold text-white/72 transition-colors hover:text-white"
                >
                  Zarejestruj się
                </button>
              </>
            ) : (
              <>
                Masz już konto?{' '}
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="font-semibold text-white/72 transition-colors hover:text-white"
                >
                  Zaloguj się
                </button>
              </>
            )}
          </p>

          <p className={cn(MICRO, 'mt-2 text-center text-white/55')}>
            Wolisz pełny formularz?{' '}
            <Link href={`/${locale}/register`} className="underline-offset-4 hover:underline">
              Rejestracja krok po kroku
            </Link>
          </p>
        </AuthCard>
      </div>

      {/* The crossing's light. One element, one pass, unmounted with the page it
          is leaving — there is nothing here to clean up. */}
      {crossing ? (
        <span
          aria-hidden="true"
          className="flou-auth-bloom pointer-events-none fixed inset-0 z-20 m-auto h-[44rem] w-[44rem] rounded-full"
        />
      ) : null}

      {/* Opened WITH the address already in the box, read at that moment — the
          visitor who mistyped their password should not retype their e-mail. */}
      <ForgotPasswordModal
        open={forgotEmail !== null}
        onClose={() => setForgotEmail(null)}
        defaultEmail={forgotEmail ?? ''}
      />
    </div>
  )
}
